import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";

interface MyStats {
  today: number;
  month: number;
  year: number;
  lifetime: number;
  active: number;
  resolved: number;
  avgRating: number | null;
  totalRated: number;
}

const StaffMyStats = () => {
  const { user } = useAuth();
  const [myStats, setMyStats] = useState<MyStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMyStats = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const uid = user.id;
    const today = format(new Date(), "yyyy-MM-dd");
    const monthStart = format(new Date(), "yyyy-MM-01");
    const yearStart = format(new Date(), "yyyy-01-01");
    const [todayR, monthR, yearR, lifetimeR, activeR, resolvedR, ratingR] = await Promise.all([
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid).gte("created_at", today),
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid).gte("created_at", monthStart),
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid).gte("created_at", yearStart),
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid),
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid).in("status", ["open", "in_progress"]),
      supabase.from("support_tickets").select("*", { count: "exact", head: true }).eq("assigned_to", uid).eq("status", "resolved"),
      supabase.from("support_tickets").select("rating").eq("assigned_to", uid).not("rating", "is", null),
    ]);
    const ratings = (ratingR.data ?? []).map((r: any) => r.rating as number);
    const avgRating = ratings.length > 0 ? ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length : null;
    setMyStats({
      today: todayR.count || 0,
      month: monthR.count || 0,
      year: yearR.count || 0,
      lifetime: lifetimeR.count || 0,
      active: activeR.count || 0,
      resolved: resolvedR.count || 0,
      avgRating,
      totalRated: ratings.length,
    });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMyStats();
  }, [fetchMyStats]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="border-border bg-card shadow-card">
              <CardContent className="p-4 text-center">
                <Skeleton className="h-8 w-12 mx-auto mb-1" />
                <Skeleton className="h-3 w-16 mx-auto" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!myStats) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Today", value: myStats.today, color: "text-primary" },
          { label: "This Month", value: myStats.month, color: "text-info" },
          { label: "This Year", value: myStats.year, color: "text-warning" },
          { label: "Lifetime", value: myStats.lifetime, color: "text-foreground" },
        ].map((m) => (
          <Card key={m.label} className="border-border bg-card shadow-card">
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
              <p className="text-xs text-muted-foreground">{m.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border bg-card shadow-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-warning">{myStats.active}</p>
            <p className="text-xs text-muted-foreground">Active Tickets</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-success">{myStats.resolved}</p>
            <p className="text-xs text-muted-foreground">Resolved Tickets</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-card">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              {myStats.avgRating !== null ? (
                <>
                  <Star className="h-5 w-5 text-warning fill-warning" />
                  <span className="text-2xl font-bold text-foreground">{myStats.avgRating.toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">/5</span>
                </>
              ) : (
                <span className="text-2xl font-bold text-muted-foreground">—</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Avg Rating ({myStats.totalRated} reviews)</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StaffMyStats;
